<template>
    <vx-expandable-card toggleable id="when" :active="active">
        <template v-slot:header>
            <div class="flex items-center pb-1 lg:pb-0">
                <span
                    class="pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                    :class="{
                        'border-r-transparent': !showDate,
                        'border-r-slate-400': showDate,
                    }"
                >
                    When
                </span>
                <template v-if="showDate && currentGroup?.group_id && (currentTab !== 'now' || !canScheduleNow)">
                    <span class="text-xs font-normal text-slate-700 sm:text-base sm:font-semibold mr-1">
                        {{ dateFormatted || '-' }}
                    </span>
                    <vx-popover v-if="dateWithTzFormatted" hover arrow placement="top">
                        <span
                            class="text-xs font-normal text-slate-700 underline decoration-dotted decoration-skip-ink-none decoration-thickness-[13.5%] underline-offset-2 underline-position-from-font sm:text-base sm:font-semibold"
                            data-test="when-date"
                        >
                            {{ tzFormatted }}
                        </span>

                        <template v-slot:content> Uses the group’s time zone. </template>
                    </vx-popover>
                </template>

                <span
                    v-else-if="showDate"
                    class="text-xs font-normal text-slate-700 sm:text-base sm:font-semibold"
                    data-test="when-date"
                >
                    {{ dateWithTzFormatted || 'at campaign launch' }}
                </span>
            </div>
        </template>

        <template v-if="editable && !props.success" v-slot:headerRight>
            <vx-button @click.stop="emit('update:active', true)" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>

        <template v-slot:content>
            <div v-if="initialCalendarLoading" class="flex justify-center items-center py-12">
                <vx-icon-loading class="animate-loading-spin text-2xl text-slate-500"></vx-icon-loading>
            </div>

            <quick-blast-rules-datepicker
                v-else-if="currentGroup?.group_id && !canScheduleNow"
                v-model="date"
                :campaignType="props.campaignType"
                class="my-4"
                isRequired
            />

            <template v-else>
                <vx-tabs v-model="currentTab">
                    <vx-tab value="now">Now</vx-tab>
                    <vx-tab value="later">Later</vx-tab>
                </vx-tabs>

                <template v-if="currentTab === 'now'">
                    <div class="text-center flex justify-center text-slate-600 text-lg font-normal my-12">
                        <span class="text-center block">
                            Your message will send immediately after clicking the launch button.
                        </span>
                    </div>
                </template>
                <quick-blast-rules-datepicker
                    v-if="currentTab === 'later'"
                    v-model="date"
                    :campaignType="props.campaignType"
                    class="my-4 lg:my-10"
                    isRequired
                />
            </template>

            <vx-alert v-if="!initialCalendarLoading" color="info">
                You should respect safe sending hours. Texting is allowed between 8 am to 9 pm in the contact's
                time-zone. We recommend scheduling between 10 am to 6 pm to drive the most engagement.
            </vx-alert>

            <vx-button
                v-if="!initialCalendarLoading"
                @click="confirm()"
                data-test="when-continue"
                class="mt-4 lg:mt-6"
                size="lg"
                block
                :disabled="currentTab === 'later' && !date"
                :loading="props.loading"
            >
                Preview
                <font-awesome-icon widthAuto class="text-base" :icon="faArrowTurnDown"></font-awesome-icon>
            </vx-button>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import {
    VxAlert,
    VxButton,
    VxExpandableCard,
    VxIconLoading,
    VxPopover,
    VxSwal,
    VxTab,
    VxTabs,
} from '@voxie/frontend-components';
import { computed, ref, toRef, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowTurnDown, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { formatTz, getTeamTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useQuickBlastWhen } from '~/components/quick-blast/useQuickBlastWhen';
import QuickBlastRulesDatepicker from './QuickBlastRulesDatepicker.vue';

const currentTab = ref('now');
const { currentGroup } = useCurrentGroup();

const props = defineProps({
    modelValue: {
        required: false,
    },
    active: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    campaignType: {
        type: String,
    },
});

const emit = defineEmits(['update:modelValue', 'update:active', 'continue']);

const {
    date,
    canScheduleNow,
    initialCalendarLoading,
    dateFormatted,
    tzFormatted,
    dateWithTzFormatted,
    campaignTypeRestricted,
    loadCurrentMonthCalendar,
} = useQuickBlastWhen(
    {
        campaignType: toRef(props, 'campaignType'),
        modelValue: toRef(props, 'modelValue'),
        currentTab,
    },
    emit
);

const calendarFetched = ref(false);

const showDate = computed(() => (props.active || props.modelValue) && !initialCalendarLoading.value);
const editable = computed(() => !props.active && props.modelValue);

watch(
    () => props.campaignType,
    () => {
        if (currentGroup.value?.group_id && campaignTypeRestricted.value) {
            date.value = undefined;
        }
    }
);

watch(
    () => props.active,
    async (newActive) => {
        if (newActive && !calendarFetched.value && !props.modelValue) {
            await loadCurrentMonthCalendar();
            calendarFetched.value = true;

            if (!canScheduleNow.value) {
                date.value = undefined;
            }
        }
    },
    { immediate: true }
);

const getStartAtOutsideSafeHours = () => {
    const liveAt = (date.value === 'now' ? dayjs() : dayjs(date.value)).tz(getTeamTz());

    const isWithinTimeRange = liveAt.isBetween(
        liveAt.startOf('day').hour(8),
        liveAt.startOf('day').hour(20),
        'hour',
        '[)' // Inclusive start, exclusive end
    );

    return !isWithinTimeRange ? liveAt.format('hh:mm a') : null;
};

const confirm = () => {
    if (currentTab.value === 'now' && canScheduleNow.value) {
        date.value = 'now';
    }

    const startAtOutsideSafeHours = getStartAtOutsideSafeHours();

    if (startAtOutsideSafeHours !== null) {
        const timezone = formatTz(dayjs(date.value).tz(getTeamTz()).format('z'));

        const html = `<div class="flex flex-col gap-3">
                <span>Your campaign is scheduled to start at <strong>${startAtOutsideSafeHours} ${timezone}</strong>, which could send messages outside of the safe sending hours of 8am to 9pm.</span>
                <span>This could lead to regulatory issues and increased opt-outs. We recommend scheduling between 10 am to 6 pm to drive the most engagement.</span>
            </div>`;

        VxSwal.fire({
            icon: 'danger',
            title: 'Outside Safe Hours!',
            html: html,
            showCancelButton: true,
            confirmButtonColor: 'danger',
            confirmButtonText: 'Proceed anyway',
        }).then(({ isConfirmed }) => {
            if (isConfirmed) {
                emit('continue');
            }
        });
    } else {
        emit('continue');
    }
};
</script>
